import { useMemo } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../../../../components/ui/molecules/table';
import { ChartCard } from '../components';
import { NonBillableSplitChartProps, PieSlice } from '../interface';
import { nonBillableSplit, sliceBadge } from '../utils';

const SURFACE = 'hsl(var(--card))';
const BADGE_HEIGHT = 18;
const BADGE_CHAR_WIDTH = 7;

interface SliceLabelProps {
  index?: number;
  cx?: number | string;
  cy?: number | string;
  midAngle?: number;
  outerRadius?: number | string;
}

interface TooltipProps {
  active?: boolean;
  payload?: { payload: PieSlice }[];
}

function SliceTooltip({ active, payload }: TooltipProps) {
  const slice = payload?.[0]?.payload;
  if (!active || !slice) return null;
  return (
    <div className="rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md">
      <p className="flex items-center gap-1.5 font-medium">
        <span aria-hidden="true" className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: slice.color }} />
        {slice.name}
      </p>
      <p className="mt-0.5 text-muted-foreground">{slice.percent}% · {slice.hours}h</p>
    </div>
  );
}

export function NonBillableSplitChart({ data, isLoading, isError }: NonBillableSplitChartProps) {
  const slices = useMemo(() => (data ? nonBillableSplit(data) : []), [data]);

  return (
    <ChartCard
      title="Non-billable hours breakdown"
      subtitle="POC, Internal and Other, share of non-billable hours"
      isLoading={isLoading}
      isError={isError}
      isEmpty={!!data && slices.length === 0}
      emptyMessage="No non-billable hours in this selection."
      chart={(
        <div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={slices}
                  dataKey="hours"
                  nameKey="name"
                  outerRadius="90%"
                  stroke={SURFACE}
                  strokeWidth={2}
                  isAnimationActive={false}
                  labelLine={false}
                  label={({ index, cx, cy, midAngle, outerRadius }: SliceLabelProps) => {
                    const slice = index === undefined ? undefined : slices[index];
                    const badge = slice
                      ? sliceBadge(slice.percent, Number(cx), Number(cy), Number(outerRadius), midAngle ?? 0)
                      : null;
                    if (!slice || !badge) return <g key={index} />;
                    // A card-colored chip with ordinary text, so it reads on every slice color in both themes.
                    const width = badge.text.length * BADGE_CHAR_WIDTH + 10;
                    return (
                      <g key={slice.key}>
                        <rect
                          x={badge.x - width / 2}
                          y={badge.y - BADGE_HEIGHT / 2}
                          width={width}
                          height={BADGE_HEIGHT}
                          rx={9}
                          fill={SURFACE}
                        />
                        <text
                          x={badge.x}
                          y={badge.y}
                          textAnchor="middle"
                          dominantBaseline="central"
                          fontSize={11}
                          fontWeight={600}
                          fill="hsl(var(--foreground))"
                        >
                          {badge.text}
                        </text>
                      </g>
                    );
                  }}
                >
                  {slices.map((s) => <Cell key={s.key} fill={s.color} />)}
                </Pie>
                <Tooltip content={<SliceTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="mt-3 space-y-1.5 text-sm">
            {slices.map((s) => (
              <li key={s.key} className="flex items-center gap-2">
                <span aria-hidden="true" className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: s.color }} />
                <span className="min-w-0 flex-1 truncate">{s.name}</span>
                <span className="shrink-0 tabular-nums text-muted-foreground">{s.hours}h · {s.percent}%</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      table={(
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Hours</TableHead>
              <TableHead>Share</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {slices.map((s) => (
              <TableRow key={s.key}>
                <TableCell>{s.name}</TableCell>
                <TableCell>{s.hours}h</TableCell>
                <TableCell>{s.percent}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    />
  );
}
